import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Link as LinkIcon, Edit as EditIcon, Label as LabelIcon } from '@mui/icons-material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import apiClient from '../utils/api';
import { FixedTagCategory, FixedTagOption } from '../types';

const Settings: React.FC = () => {

  // 固定タグ管理の状態
  const [fixedCategories, setFixedCategories] = useState<FixedTagCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newOptionName, setNewOptionName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [editDialog, setEditDialog] = useState<{ open: boolean; type: 'category' | 'option'; id: string; name: string }>({ open: false, type: 'category', id: '', name: '' });

  const loadFixedTags = async () => {
    try {
      const res = await apiClient.getFixedTagCategories();
      if (res.success) setFixedCategories(res.categories);
    } catch (e) {
      console.error('固定タグ読み込みエラー:', e);
    }
  };

  useEffect(() => {
    loadFixedTags();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await apiClient.createFixedTagCategory(newCategoryName.trim(), fixedCategories.length + 1);
      if (res.success) {
        setNewCategoryName('');
        loadFixedTags();
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'カテゴリの追加に失敗しました');
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!window.confirm(`カテゴリ「${name}」を削除しますか？\n配下の選択肢も全て削除されます。`)) return;
    try {
      await apiClient.deleteFixedTagCategory(id);
      loadFixedTags();
    } catch (e) {
      alert('削除に失敗しました');
    }
  };

  const handleAddOption = async () => {
    if (!selectedCategoryId || !newOptionName.trim()) return;
    const cat = fixedCategories.find(c => c.id === selectedCategoryId);
    const order = (cat?.options?.length || 0) + 1;
    try {
      const res = await apiClient.createFixedTagOption(selectedCategoryId, newOptionName.trim(), order);
      if (res.success) {
        setNewOptionName('');
        loadFixedTags();
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || '選択肢の追加に失敗しました');
    }
  };

  const handleDeleteOption = async (id: string, name: string) => {
    if (!window.confirm(`選択肢「${name}」を削除しますか？`)) return;
    try {
      await apiClient.deleteFixedTagOption(id);
      loadFixedTags();
    } catch (e) {
      alert('削除に失敗しました');
    }
  };

  const handleEditSave = async () => {
    if (!editDialog.name.trim()) return;
    try {
      if (editDialog.type === 'category') {
        await apiClient.updateFixedTagCategory(editDialog.id, editDialog.name.trim());
      } else {
        await apiClient.updateFixedTagOption(editDialog.id, editDialog.name.trim());
      }
      setEditDialog({ open: false, type: 'category', id: '', name: '' });
      loadFixedTags();
    } catch (e: any) {
      alert(e?.response?.data?.error || '更新に失敗しました');
    }
  };

  // 固定IPアドレス管理の状態
  const [ipEnabled, setIpEnabled] = useState<boolean>(false);
  const [allowedIps, setAllowedIps] = useState<{ id: string; ip: string; note?: string; created_at?: string }[]>([]);
  const [newIp, setNewIp] = useState('');
  const [newNote, setNewNote] = useState('');
  const [myIp, setMyIp] = useState<string>('');
  const [ipError, setIpError] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const list = await apiClient.getAllowedIps();
        setIpEnabled(!!list.enabled);
        // バックエンドから返ってくるデータ形式に合わせて変換
        const mappedItems = Array.isArray(list.items) 
          ? list.items.map((item) => ({
              id: item.id,
              ip: item.ip_address, // ip_addressをipにマッピング
              note: item.description, // descriptionをnoteにマッピング
              created_at: item.created_at
            }))
          : [];
        setAllowedIps(mappedItems);
      } catch (e) {
        console.warn('許可IP取得に失敗:', e);
      }
      try {
        const info = await apiClient.getMyIp();
        setMyIp(info?.ip || '');
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const ipv4Regex = useMemo(() => /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/,
    []);
  const ipv6Regex = useMemo(() => /^[0-9a-fA-F:]+$/,
    []);

  const validateIp = (ip: string) => {
    if (!ip) return 'IPアドレスを入力してください';
    if (ipv4Regex.test(ip)) return '';
    if (ipv6Regex.test(ip)) return '';
    return 'IPアドレス形式が正しくありません（IPv4/IPv6）';
  };

  const handleAddIp = async () => {
    const err = validateIp(newIp.trim());
    if (err) { setIpError(err); return; }
    setIpError('');
    try {
      const res = await apiClient.addAllowedIp(newIp.trim(), newNote.trim());
      // バックエンドから返ってくるデータ形式に合わせて変換
      const mappedItem = {
        id: res.item.id,
        ip: res.item.ip_address,
        note: res.item.description,
        created_at: res.item.created_at
      };
      setAllowedIps(prev => [mappedItem, ...prev]);
      setNewIp('');
      setNewNote('');
    } catch (e: any) {
      alert(e?.response?.data?.message || '登録に失敗しました');
    }
  };

  const handleRemoveIp = async (id: string) => {
    if (!window.confirm('このIPを削除しますか？')) return;
    try {
      await apiClient.removeAllowedIp(id);
      setAllowedIps(prev => prev.filter(it => it.id !== id));
    } catch (e) {
      alert('削除に失敗しました');
    }
  };

  const handleToggleEnabled = async (value: boolean) => {
    try {
      const res = await apiClient.setAllowedIpsEnabled(value);
      setIpEnabled(!!res.enabled);
    } catch (e) {
      alert('切り替えに失敗しました');
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          システム設定
        </Typography>

        {/* 固定IPアドレス管理 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            固定IPアドレス管理（/admin アクセス制御）
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch checked={ipEnabled} onChange={(e) => handleToggleEnabled(e.target.checked)} />
              }
              label={ipEnabled ? '有効' : '無効'}
            />
            {myIp && (
              <Tooltip title="現在の接続元IP">
                <Chip icon={<LinkIcon />} label={`現在のIP: ${myIp}`} variant="outlined" />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              label="IPアドレス (IPv4/IPv6)"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              error={!!ipError}
              helperText={ipError || '例: 150.249.205.37 / 240d:1b:...'}
              size="small"
              sx={{ minWidth: 280 }}
            />
            <TextField
              label="備考"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddIp}>
              追加
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>IPアドレス</TableCell>
                <TableCell>備考</TableCell>
                <TableCell>登録日時</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allowedIps.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.ip}</TableCell>
                  <TableCell>{row.note || '-'}</TableCell>
                  <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="削除">
                      <IconButton onClick={() => handleRemoveIp(row.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {allowedIps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    登録されたIPはありません
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* 固定タグ管理 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon />
            固定タグ管理（商材・対応部署）
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            チャットルームに設定できる固定タグのカテゴリと選択肢を管理します。
          </Typography>

          {/* カテゴリ追加 */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center' }}>
            <TextField
              label="新しいカテゴリ名"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCategory}>
              カテゴリ追加
            </Button>
          </Box>

          {/* カテゴリ一覧 */}
          {fixedCategories.map((category) => (
            <Box key={category.id} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {category.name}
                </Typography>
                <Box>
                  <Tooltip title="編集">
                    <IconButton size="small" onClick={() => setEditDialog({ open: true, type: 'category', id: category.id, name: category.name })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="削除">
                    <IconButton size="small" onClick={() => handleDeleteCategory(category.id, category.name)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* 選択肢一覧 */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {category.options && category.options.length > 0 ? (
                  category.options.map((option) => (
                    <Chip
                      key={option.id}
                      label={option.name}
                      variant="outlined"
                      size="small"
                      onDelete={() => handleDeleteOption(option.id, option.name)}
                      onClick={() => setEditDialog({ open: true, type: 'option', id: option.id, name: option.name })}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    選択肢がありません
                  </Typography>
                )}
              </Box>

              {/* 選択肢追加 */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={`${category.name}の選択肢を追加`}
                  value={selectedCategoryId === category.id ? newOptionName : ''}
                  onChange={(e) => {
                    setSelectedCategoryId(category.id);
                    setNewOptionName(e.target.value);
                  }}
                  onFocus={() => setSelectedCategoryId(category.id)}
                  size="small"
                  sx={{ minWidth: 200 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setSelectedCategoryId(category.id);
                      handleAddOption();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedCategoryId(category.id);
                    setTimeout(handleAddOption, 0);
                  }}
                >
                  追加
                </Button>
              </Box>
            </Box>
          ))}

          {fixedCategories.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              固定タグカテゴリが登録されていません
            </Typography>
          )}
        </Paper>

        {/* 編集ダイアログ */}
        <Dialog open={editDialog.open} onClose={() => setEditDialog({ ...editDialog, open: false })}>
          <DialogTitle>{editDialog.type === 'category' ? 'カテゴリ名を編集' : '選択肢名を編集'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              value={editDialog.name}
              onChange={(e) => setEditDialog({ ...editDialog, name: e.target.value })}
              label="名前"
              sx={{ mt: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleEditSave()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog({ ...editDialog, open: false })}>キャンセル</Button>
            <Button variant="contained" onClick={handleEditSave}>保存</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </AdminLayout>
  );
};

export default Settings; 